import axios from "axios";

// Base URL for all API calls to the FastAPI backend
export const BASE_URL =
  process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

const API = axios.create({
  baseURL: BASE_URL,
  timeout: 120000,
});

// Automatically attaches JWT token to every request if available
API.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Automatically refreshes the access token when a 401 is received
API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Surface upload-specific errors clearly so users understand what went wrong
    if (error.response?.status === 413) {
      return Promise.reject({
        ...error,
        userMessage: "File is too large for this document slot. Please check the maximum file size shown in the upload window and try a smaller file.",
      });
    }

    // Network timeout (proxy cut the connection while uploading/processing)
    if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
      return Promise.reject({
        ...error,
        userMessage:
          "The upload timed out. The file may be too large or the server is busy. Please try again.",
      });
    }

    // If 401 and we haven't already tried to refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem("refresh_token");
      if (!refreshToken) {
        // No refresh token — force logout
        localStorage.removeItem("token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("user");
        window.location.href = "/#/login";
        return Promise.reject(error);
      }

      try {
        const res = await API.post("/auth/refresh", {
          refresh_token: refreshToken,
        });
        const newToken = res.data.access_token;

        // Update stored token
        localStorage.setItem("token", newToken);
        API.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
        originalRequest.headers["Authorization"] = `Bearer ${newToken}`;

        // Retry the original request with new token
        return API(originalRequest);
      } catch (refreshError) {
        // Refresh failed — force logout
        localStorage.removeItem("token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("user");
        window.location.href = "/#/login";
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);


// Authentication endpoints
export const login = async (credentials) => {
  const response = await API.post("/auth/login", credentials);
  if (response.data.refresh_token) {
    localStorage.setItem("refresh_token", response.data.refresh_token);
  }
  return response;
};
export const refreshAccessToken = (refreshToken) =>
  API.post("/auth/refresh", { refresh_token: refreshToken });
export const logout = (refreshToken) =>
  API.post("/auth/logout", { refresh_token: refreshToken });
export const register = (data) => API.post("/auth/register", data);

export const sendOTP = (email, purpose, applicationType) =>
  API.post('/auth/send-otp', { email, purpose, application_type: applicationType });

export const verifyOTP = (email, otp_code, purpose) =>
  API.post('/auth/verify-otp', { email, otp_code, purpose });

export const forgotPassword = (email) =>
  API.post('/auth/forgot-password', { email });

export const resetPassword = (email, otp_code, new_password) =>
  API.post('/auth/reset-password', { email, otp_code, new_password });

export const acceptTerms = () =>
  API.post('/auth/accept-terms');

export const getCurrentUser = () => API.get("/auth/me");
export const getAllUsers = () => API.get("/auth/users");
export const updateUserStatus = (id, status) =>
  API.patch(`/auth/users/${id}/status?status=${status}`);
export const getRegistrationStatus = (email, applicationType) => {
  const params = applicationType ? `?application_type=${encodeURIComponent(applicationType)}` : '';
  return API.get(`/auth/registration-status/${encodeURIComponent(email)}${params}`);
};
export const updateMyProfile = (data) => API.patch("/auth/me", data);
export const uploadAvatar = (formData) => API.post("/auth/me/avatar", formData);
export const deleteAvatar = () => API.delete("/auth/me/avatar");
export const getAvatarUrl = (userId, path = null) => {
  if (path && path.startsWith("http")) {
    return path;
  }
  return `${BASE_URL}/auth/avatar/${userId}`;
};

// Organization endpoints
export const getAllOrganizations = () => API.get("/organizations/");
export const getPendingOrganizations = () => API.get("/organizations/pending");
export const updateOrganizationStatus = (id, data) =>
  API.patch(`/organizations/${id}/status`, data);
export const updateOffcampusOrgStatus = (orgId, data) =>
  API.patch(`/organizations/${orgId}/offcampus-status`, data);
export const updateOrganization = (id, data) =>
  API.patch(`/organizations/${id}`, data);
// export const getDashboardStats = () => API.get("/organizations/dashboard/stats");
// export const getAIDocumentStats = () => API.get("/organizations/dashboard/ai-analytics");
export const deleteOrganization = (id) => API.delete(`/organizations/${id}`);
export const getMyOrganization = () => API.get("/organizations/me");
export const updateMyOrganization = (data) => API.patch("/organizations/me", data);
export const downloadOrgDetails = (orgId) =>
  API.get(`/organizations/${orgId}/download`, { responseType: 'blob' });
export const downloadAllOrgs = () =>
  API.get(`/organizations/download-all`, {
    responseType: 'blob',
    timeout: 600000  // 10 minutes
  });
export const downloadOffcampusDetails = (orgId) =>
  API.get(`/organizations/offcampus/${orgId}/download`, { responseType: 'blob' });
export const downloadAllOffcampus = () =>
  API.get(`/organizations/offcampus/download-all`, { responseType: 'blob' });
export const downloadByDocumentType = (documentName) =>
  API.get(`/organizations/download/by-document`, {
    params: { document_name: documentName },
    responseType: 'blob'
  });
export const downloadAllByType = () =>
  API.get(`/organizations/download/all-by-type`, {
    responseType: 'blob',
    timeout: 600000  // 10 minutes
  });
export const downloadOrgList = () =>
  API.get(`/organizations/download/org-list`, {
    responseType: 'blob'
  });
// Public list of all organizations — used by the Existing Org (Handover) signup dropdown.
// No auth token required; uses the base axios instance directly.
export const getOrganizationsList = () =>
  axios.get(`${BASE_URL}/auth/organizations/list`);
export const checkOrgName = (name) =>
  axios.get(`${BASE_URL}/auth/check-org-name?name=${encodeURIComponent(name)}`);

// Document endpoints
export const uploadAccreditationDocument = (category, formData, options = {}) =>
  API.post(`/documents/${category}/upload`, formData, {
    timeout: 300000,
    ...options,
  });
export const uploadBulkAccreditationDocuments = (category, formData, options = {}) =>
  API.post(`/documents/${category}/bulk-upload`, formData, {
    timeout: 300000,
    ...options,
  });
export const uploadOffcampusDocument = (formData, options = {}) =>
  API.post("/documents/offcampus/upload", formData, {
    timeout: 300000,
    ...options,
  });
export const uploadBulkOffcampusDocuments = (formData, options = {}) =>
  API.post("/documents/offcampus/bulk-upload", formData, {
    timeout: 300000,
    ...options,
  });
export const getAccreditationDocuments = (category, orgId) =>
  API.get(`/documents/${category}/${orgId}`);
export const getOffcampusDocuments = (orgId) =>
  API.get(`/documents/offcampus/${orgId}`);
export const updateAccreditationStatus = (category, id, data) =>
  API.patch(`/documents/${category}/${id}/status`, data);
export const updateOffcampusStatus = (id, data) =>
  API.patch(`/documents/offcampus/${id}/status`, data);
export const downloadAccreditationDocument = (category, docId) =>
  API.get(`/documents/${category}/${docId}/download`, {
    responseType: "blob",
  });
export const downloadOffcampusDocument = (docId) =>
  API.get(`/documents/offcampus/${docId}/download`, { responseType: "blob" });
export const deleteAccreditationDocument = (category, documentId) =>
  API.delete(`/documents/${category}/${documentId}`);
export const deleteOffcampusDocument = (documentId) =>
  API.delete(`/documents/offcampus/${documentId}`);
export const uploadChedCertificate = (orgId, formData) =>
  API.post(`/documents/offcampus/${orgId}/ched-certificate`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
export const getChedCertificate = (orgId) =>
  API.get(`/documents/offcampus/${orgId}/ched-certificate`);
export const deleteChedCertificate = (orgId) =>
  API.delete(`/documents/offcampus/${orgId}/ched-certificate`);
export const uploadAccreditationCertificate = (orgId, formData) =>
  API.post(`/documents/accreditation-cert/${orgId}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
export const getAccreditationCertificate = (orgId) =>
  API.get(`/documents/accreditation-cert/${orgId}/get`);
export const deleteAccreditationCertificate = (orgId) =>
  API.delete(`/documents/accreditation-cert/${orgId}/delete`);

export const submitWaiverLink = (data) =>
  API.post('/documents/waiver-link', data);

export const getWaiverLink = (organizationId, applicationType) =>
  API.get(`/documents/waiver-link/${organizationId}`, {
    params: { application_type: applicationType }
  });

export const deleteWaiverLink = (organizationId, applicationType) =>
  API.delete(`/documents/waiver-link/${organizationId}`, {
    params: { application_type: applicationType }
  });

// Checklist endpoints
export const getAccreditationChecklist = (orgId) =>
  API.get(`/checklist/accreditation/${orgId}`);
export const getOffcampusChecklist = (orgId) =>
  API.get(`/checklist/offcampus/${orgId}`);
export const getAccreditationDocumentList = () =>
  API.get("/checklist/documents/accreditation");
export const getReaccreditationDocumentList = () =>
  API.get("/checklist/documents/reaccreditation");
export const getOffcampusDocumentList = () =>
  API.get("/checklist/documents/offcampus");
export const getAllRequiredDocuments = () =>
  API.get("/checklist/documents/all");
// Public endpoint — no auth needed — returns full objects (id, name, file path) for org user preview
export const getPublicRequiredDocuments = async () => {
  try {
    // Try the dedicated main.py public endpoint first (has full file paths)
    const res = await API.get("/requirements/public");
    return { data: res.data };
  } catch {
    // Fallback: use the 3 existing checklist endpoints (returns names only, no preview)
    const [accRes, reaccRes, offRes] = await Promise.all([
      API.get("/checklist/documents/accreditation"),
      API.get("/checklist/documents/reaccreditation"),
      API.get("/checklist/documents/offcampus"),
    ]);
    const toFull = (res, category) =>
      res.data.full ||
      (res.data.documents || []).map((name, i) => ({
        required_document_id: i,
        document_name: name,
        template_file_path: null,
        category,
      }));
    return {
      data: {
        accreditation: toFull(accRes, "accreditation"),
        reaccreditation: toFull(reaccRes, "reaccreditation"),
        offcampus: toFull(offRes, "offcampus"),
      },
    };
  }
};
export const addRequiredDocument = (data) =>
  API.post("/checklist/documents", data, {
    headers: { "Content-Type": "multipart/form-data" },
  });
export const deleteRequiredDocument = (category, id) =>
  API.delete(`/checklist/documents/${category}/${id}`);

// Remarks endpoints
export const addAccreditationRemark = (category, docId, content, status = null) => {
  const params = new URLSearchParams();
  if (content !== undefined && content !== null) params.append("content", content);
  if (status) params.append("status", status);
  return API.post(`/remarks/${category}/${docId}?${params.toString()}`);
};
export const addOffcampusRemark = (docId, content, status = null) => {
  const params = new URLSearchParams();
  if (content !== undefined && content !== null) params.append("content", content);
  if (status) params.append("status", status);
  return API.post(`/remarks/offcampus/${docId}?${params.toString()}`);
};
export const getAccreditationRemarks = (category, docId) =>
  API.get(`/remarks/${category}/${docId}`);
export const getOffcampusRemarks = (docId) =>
  API.get(`/remarks/offcampus/${docId}`);
// Bulk: single call returns all remarks for an org grouped by document_id
export const getBulkAccreditationRemarks = (category, orgId) =>
  API.get(`/remarks/bulk/${category}/${orgId}`);
export const getBulkOffcampusRemarks = (orgId) =>
  API.get(`/remarks/bulk/offcampus/${orgId}`);

// Announcement endpoints
export const getAnnouncements = () => API.get("/announcements/");
export const getAllAnnouncementsAdmin = () => API.get("/announcements/all");
export const createAnnouncement = (formData) =>
  API.post("/announcements/", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
export const updateAnnouncement = (id, formData) =>
  API.patch(`/announcements/${id}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
export const deleteAnnouncement = (id) => API.delete(`/announcements/${id}`);
export const dismissAnnouncement = (announcementId) =>
  API.post(`/announcements/${announcementId}/dismiss`);
export const restoreAnnouncement = (announcementId) =>
  API.post(`/announcements/${announcementId}/restore`);
export const getArchivedAnnouncements = () =>
  API.get("/announcements/archived");
// Resolves an announcement image to a displayable URL (http URLs are used
// directly; local paths are served by the backend)
export const getAnnouncementImageUrl = (announcement) => {
  if (!announcement || !announcement.image_path) return null;
  if (announcement.image_path.startsWith("http")) return announcement.image_path;
  return `${BASE_URL}/announcements/${announcement.announcement_id}/image`;
};

// Notification endpoints
export const getMyNotifications = () => API.get("/notifications/me");
export const markNotificationRead = (id) =>
  API.patch(`/notifications/${id}/read`);
export const markAllNotificationsRead = () =>
  API.patch("/notifications/mark-all-read");
export const getUnreadNotificationCount = () =>
  API.get("/notifications/me/unread-count");

// Report endpoints
export const getComplianceReport = () => API.get("/reports/compliance");
export const getOrganizationComplianceReport = (orgId) =>
  API.get(`/reports/compliance/${orgId}`);
export const getTrends = (days = 30) => API.get(`/reports/trends?days=${days}`);
export const getAIDocumentStats = (days = 30) =>
  API.get(`/reports/document-analytics?days=${days}`);
export const getDashboardStats = () => API.get("/reports/dashboard-stats");

// Academic Year endpoints
export const getCurrentAcademicYear = () => API.get("/academic-year/current");
export const getAllAcademicYears = () => API.get("/academic-year/all");
export const createAcademicYear = (data) => API.post("/academic-year/", data);
export const endAcademicYear = (id) => API.patch(`/academic-year/${id}/end`);
export const updateAcademicYearLockDate = (id, lockDate) =>
  API.patch(`/academic-year/${id}/lock-date`, { lock_date: lockDate });
export const updateReminderSchedule = (id, reminderSchedule) =>
  API.patch(`/academic-year/${id}/reminder-schedule`, {
    reminder_schedule: reminderSchedule
  });
export const updateGracePeriod = (id, gracePeriodDays) =>
  API.patch(`/academic-year/${id}/grace-period`, {
    grace_period_days: gracePeriodDays
  });

export const getCurrentOffcampusYear = () =>
  API.get('/offcampus-academic-year/current');

export const getAllOffcampusYears = () =>
  API.get('/offcampus-academic-year/');

export const createOffcampusAcademicYear = (data) =>
  API.post('/offcampus-academic-year/', data);

export const updateOffcampusAcademicYear = (id, data) =>
  API.patch(`/offcampus-academic-year/${id}`, data);

export const endOffcampusAcademicYear = (id) =>
  API.patch(`/offcampus-academic-year/${id}/end`);

// ─── Participant Portal endpoints ────────────────────────────────────────────
// PIC (User) — manage links
export const createParticipantLink = (data) =>
  API.post("/participant/links", data);
export const getMyParticipantLinks = () =>
  API.get("/participant/links/me");
export const updateParticipantLink = (linkId, data) =>
  API.patch(`/participant/links/${linkId}`, data);
export const deleteParticipantLink = (linkId) =>
  API.delete(`/participant/links/${linkId}`);
export const getLinkSubmissions = (linkId) =>
  API.get(`/participant/links/${linkId}/submissions`);
export const downloadSubmissionZip = (linkId, docType) =>
  API.get(`/participant/links/${linkId}/download/${docType}`, {
    responseType: "blob",
  });
export const mergeParticipantDocuments = (linkId, docType) =>
  API.get(`/participant/links/${linkId}/merge/${docType}`, {
    responseType: "blob",
  });
export const mergeCustomFiles = (formData) =>
  API.post("/participant/merge-files", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    responseType: "blob",
  });

// Public — participant-facing (no auth)
export const getPortalInfo = (token) =>
  axios.get(`${BASE_URL}/participant/portal/${token}`);
export const checkParticipantEmail = (token, email) =>
  axios.get(`${BASE_URL}/participant/portal/${token}/check-email`, { params: { email } });
export const submitParticipantDocument = (token, formData) =>
  axios.post(`${BASE_URL}/participant/portal/${token}/submit`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });


// ─── Admin Staff Management endpoints ────────────────────────────────────────
export const getAdminStaff = () =>
  API.get("/auth/admin/staff");
export const inviteAdminStaff = (data) =>
  API.post("/auth/admin/invite", data);
export const updateAdminStaff = (userId, data) =>
  API.patch(`/auth/admin/staff/${userId}`, data);
export const deleteAdminStaff = (userId) =>
  API.delete(`/auth/admin/staff/${userId}`);
export const adminSetPassword = (data) =>
  API.post("/auth/admin/set-password", data);

export default API;
// Force trigger vercel
